import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, type SyncReportView } from '@/client/api';
import { publicCalendarUrlFor } from '@/client/calendar-links';
import { Page, PageHeader, SectionTitle } from '@/client/components/AppShell';
import { ExternalIcon, RefreshIcon } from '@/client/components/icons';
import { RiderEditor, RiderRow } from '@/client/components/riders';
import {
  Badge,
  Button,
  Card,
  ErrorNotice,
  Field,
  Input,
  SkeletonList,
  Toggle,
} from '@/client/components/ui';
import {
  SESSION_KEY,
  useSaveSettings,
  useSession,
  useWorkspace,
  WORKSPACE_KEY,
} from '@/client/store';
import { DAY_TYPES, type DayType, type Rider } from '@/shared/types';

export function Settings() {
  const { data: workspace, isPending } = useWorkspace();
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const saveSettings = useSaveSettings();

  const [syncResult, setSyncResult] = useState<SyncReportView | null>(null);
  const [calendarNote, setCalendarNote] = useState<string | null>(null);
  const [editingRider, setEditingRider] = useState<Rider | 'new' | null>(null);

  const setupCalendars = useMutation({
    mutationFn: api.setupCalendars,
    onSuccess: (data) => {
      queryClient.setQueryData(WORKSPACE_KEY, data.workspace);
      setCalendarNote(data.note ?? null);
    },
  });

  const sync = useMutation({
    mutationFn: () => api.syncCalendars(false),
    onSuccess: (data) => {
      queryClient.setQueryData(WORKSPACE_KEY, data.workspace);
      setSyncResult(data.report);
    },
  });

  const signOut = useMutation({
    mutationFn: api.logout,
    onSuccess: async () => {
      queryClient.clear();
      await queryClient.invalidateQueries({ queryKey: SESSION_KEY });
      window.location.href = '/';
    },
  });

  if (isPending || !workspace) {
    return (
      <>
        <PageHeader title="Settings" />
        <Page>
          <SkeletonList rows={4} />
        </Page>
      </>
    );
  }

  const { settings } = workspace;
  const calendarsReady = Boolean(settings.publicCalendarId && settings.assignmentCalendarId);

  const update = (patch: Partial<typeof settings>) => {
    saveSettings.mutate({
      rollingWindowWeeks: settings.rollingWindowWeeks,
      coordinatorHorizonWeeks: settings.coordinatorHorizonWeeks,
      calendarSyncHorizonWeeks: settings.calendarSyncHorizonWeeks,
      meetingDays: settings.meetingDays,
      timezone: settings.timezone,
      meetingTimes: settings.meetingTimes,
      meetingDurationMinutes: settings.meetingDurationMinutes,
      meetingLocation: settings.meetingLocation,
      ...patch,
    });
  };

  const toggleDay = (day: DayType, on: boolean) => {
    const next = on
      ? [...new Set([...settings.meetingDays, day])]
      : settings.meetingDays.filter((d) => d !== day);
    // At least one meeting day, or generation has nothing to schedule.
    if (next.length === 0) return;
    update({ meetingDays: next as DayType[] });
  };

  return (
    <>
      <PageHeader title="Settings" subtitle={session?.email} />

      <Page>
        <section className="flex flex-col gap-2">
          <SectionTitle title="Your data" />
          <Card className="flex flex-col gap-3 px-4 py-4">
            <p className="text-sm text-ink-muted">
              Everything lives in one spreadsheet in your Google Drive. This app has no database —
              you can open, edit, export, or delete the file at any time.
            </p>
            <a
              href={workspace.spreadsheetUrl}
              target="_blank"
              rel="noreferrer"
              className="tap-target inline-flex items-center gap-2 self-start rounded-xl border border-border-strong px-4 text-sm font-medium"
            >
              <ExternalIcon className="size-4" />
              Open the spreadsheet
            </a>
          </Card>
        </section>

        <section className="flex flex-col gap-2">
          <SectionTitle title="Meetings" />
          <Card className="flex flex-col gap-5 px-4 py-4">
            <div className="flex flex-col gap-1">
              <Toggle
                label="Thursdays"
                checked={settings.meetingDays.includes('Thursday')}
                onChange={(on) => toggleDay('Thursday', on)}
              />
              <Toggle
                label="Sundays"
                checked={settings.meetingDays.includes('Sunday')}
                onChange={(on) => toggleDay('Sunday', on)}
              />
            </div>

            {/* One time per day: the Thursday and Sunday meetings rarely start
                at the same hour. */}
            <div className="grid grid-cols-2 gap-3">
              {DAY_TYPES.map((day) => (
                <Field key={day} label={`${day} start`}>
                  {({ id }) => (
                    <Input
                      id={id}
                      type="time"
                      defaultValue={settings.meetingTimes[day]}
                      onBlur={(event) =>
                        event.target.value &&
                        event.target.value !== settings.meetingTimes[day] &&
                        update({
                          meetingTimes: { ...settings.meetingTimes, [day]: event.target.value },
                        })
                      }
                    />
                  )}
                </Field>
              ))}
            </div>

            <Field label="Length (minutes)">
              {({ id }) => (
                <Input
                  id={id}
                  type="number"
                  inputMode="numeric"
                  min={15}
                  max={720}
                  defaultValue={settings.meetingDurationMinutes}
                  onBlur={(event) => {
                    const value = Number(event.target.value);
                    if (value >= 15 && value <= 720) update({ meetingDurationMinutes: value });
                  }}
                />
              )}
            </Field>

            <Field label="Location" hint="Optional. Added to calendar events so Maps can route.">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  defaultValue={settings.meetingLocation}
                  onBlur={(event) => update({ meetingLocation: event.target.value })}
                />
              )}
            </Field>

            <Field label="Timezone">
              {({ id }) => (
                <Input
                  id={id}
                  defaultValue={settings.timezone}
                  onBlur={(event) => event.target.value && update({ timezone: event.target.value })}
                />
              )}
            </Field>
          </Card>
        </section>

        <section className="flex flex-col gap-2">
          <SectionTitle
            title="Riders"
            hint="Who needs rides. An email address here puts them on their own ride invite."
          />
          <Card className="flex flex-col gap-3 px-2 py-3">
            <ul className="flex flex-col">
              {workspace.riders.map((rider) => (
                <RiderRow key={rider.id} rider={rider} onOpen={() => setEditingRider(rider)} />
              ))}
            </ul>
            <Button
              variant="secondary"
              onClick={() => setEditingRider('new')}
              className="mx-2 self-start"
            >
              Add rider
            </Button>
          </Card>
        </section>

        <section className="flex flex-col gap-2">
          <SectionTitle
            title="Fairness"
            hint="Tune these if the rotation stops matching what you see in Reports."
          />
          <Card className="grid gap-3 px-4 py-4 sm:grid-cols-3">
            <Field label="Rolling window" hint="weeks">
              {({ id, describedBy }) => (
                <NumberSetting
                  id={id}
                  describedBy={describedBy}
                  value={settings.rollingWindowWeeks}
                  min={1}
                  max={104}
                  onCommit={(rollingWindowWeeks) => update({ rollingWindowWeeks })}
                />
              )}
            </Field>
            <Field label="Plan ahead" hint="weeks">
              {({ id, describedBy }) => (
                <NumberSetting
                  id={id}
                  describedBy={describedBy}
                  value={settings.coordinatorHorizonWeeks}
                  min={1}
                  max={104}
                  onCommit={(coordinatorHorizonWeeks) => update({ coordinatorHorizonWeeks })}
                />
              )}
            </Field>
            <Field label="Notice window" hint="weeks">
              {({ id, describedBy }) => (
                <NumberSetting
                  id={id}
                  describedBy={describedBy}
                  value={settings.calendarSyncHorizonWeeks}
                  min={1}
                  max={52}
                  onCommit={(calendarSyncHorizonWeeks) => update({ calendarSyncHorizonWeeks })}
                />
              )}
            </Field>
          </Card>
        </section>

        <section className="flex flex-col gap-2">
          <SectionTitle
            title="Calendars"
            hint="A shared calendar showing who is driving, plus private invites for the volunteers."
          />
          <Card className="flex flex-col gap-3 px-4 py-4">
            {calendarsReady ? (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone="positive">Set up</Badge>
                  <a
                    href={publicCalendarUrlFor(settings.publicCalendarId!)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm font-medium text-brand underline underline-offset-2"
                  >
                    View the shared calendar
                  </a>
                </div>
                <p className="text-xs text-ink-muted">
                  Confirmed rides show the volunteer's name. Everything else stays as “?”, and the
                  shared calendar never carries email addresses.
                </p>
                <Button
                  variant="secondary"
                  busy={sync.isPending}
                  onClick={() => sync.mutate()}
                  className="self-start"
                >
                  <RefreshIcon className="size-4" />
                  Sync now
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm text-ink-muted">
                  Create two calendars: one you can share publicly, and one private calendar that
                  sends each volunteer their own invite.
                </p>
                <Button
                  variant="primary"
                  busy={setupCalendars.isPending}
                  onClick={() => setupCalendars.mutate()}
                  className="self-start"
                >
                  Set up calendars
                </Button>
              </>
            )}

            {calendarNote ? (
              <p className="rounded-xl bg-caution-soft px-3 py-2 text-xs">{calendarNote}</p>
            ) : null}
            {setupCalendars.isError ? <ErrorNotice error={setupCalendars.error} /> : null}
            {sync.isError ? <ErrorNotice error={sync.error} /> : null}
            {syncResult ? <SyncSummary report={syncResult} /> : null}
          </Card>
        </section>

        <section className="flex flex-col gap-2">
          <SectionTitle title="Account" />
          <Card className="flex flex-col gap-3 px-4 py-4">
            <p className="text-sm text-ink-muted">
              Signed in as <span className="font-medium text-ink">{session?.email}</span>.
            </p>
            <Button
              variant="danger"
              busy={signOut.isPending}
              onClick={() => signOut.mutate()}
              className="self-start"
            >
              Sign out
            </Button>
            <p className="text-xs text-ink-faint">
              Signing out revokes this app's access to your Google account. Your spreadsheet and
              calendars stay exactly where they are.
            </p>
          </Card>
        </section>
      </Page>

      {editingRider ? (
        <RiderEditor
          rider={editingRider === 'new' ? null : editingRider}
          onClose={() => setEditingRider(null)}
        />
      ) : null}
    </>
  );
}

/** Commits on blur so a half-typed number never lands in the Sheet. */
function NumberSetting({
  id,
  describedBy,
  value,
  min,
  max,
  onCommit,
}: {
  id: string;
  describedBy?: string;
  value: number;
  min: number;
  max: number;
  onCommit: (next: number) => void;
}) {
  return (
    <Input
      id={id}
      aria-describedby={describedBy}
      type="number"
      inputMode="numeric"
      min={min}
      max={max}
      defaultValue={value}
      onBlur={(event) => {
        const next = Number(event.target.value);
        if (Number.isInteger(next) && next >= min && next <= max && next !== value) {
          onCommit(next);
        } else {
          event.target.value = String(value);
        }
      }}
    />
  );
}

function SyncSummary({ report }: { report: SyncReportView }) {
  const nothingHappened = report.created + report.retitled + report.replaced + report.deleted === 0;

  return (
    <div className="flex flex-col gap-2 rounded-xl bg-surface-sunken px-3 py-2.5 text-xs">
      {nothingHappened ? (
        <p>Everything was already up to date ({report.unchanged} rides checked).</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {report.created > 0 ? <Badge tone="positive">{report.created} created</Badge> : null}
          {report.retitled > 0 ? <Badge tone="brand">{report.retitled} updated</Badge> : null}
          {report.replaced > 0 ? <Badge tone="caution">{report.replaced} reassigned</Badge> : null}
          {report.deleted > 0 ? <Badge>{report.deleted} removed</Badge> : null}
        </div>
      )}

      {report.warnings.map((warning) => (
        <p key={warning} className="text-caution">
          {warning}
        </p>
      ))}
      {report.errors.map((error) => (
        <p key={error} className="text-critical">
          {error}
        </p>
      ))}
    </div>
  );
}
